import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faEnvelope, faLock } from '@fortawesome/free-solid-svg-icons';
import { loginUser, googleAuth } from '../services/api';
import { APP_ROUTES } from '../config/api.config';
import GoogleAuthButton from '../components/GoogleAuthButton';

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px;
  box-sizing: border-box;
`;

const HomeLink = styled(Link)`
  position: absolute;
  top: 10px;
  left: 10px;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  font-size: 14px;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

const LoginCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 380px;
  margin: auto;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: transform 0.3s ease;
  position: relative;

  @media (min-width: 1024px) {
    max-width: 480px;
    padding: 40px;
  }

  @media (max-height: 600px) {
    padding: 15px;
  }

  &:hover {
    transform: translateY(-2px);
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
  text-align: center;
  background: linear-gradient(135deg, #ff6b6b, #ff8e53);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;

  @media (min-width: 1024px) {
    font-size: 32px;
    margin-bottom: 12px;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  color: #666;
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 1.4;

  @media (min-width: 1024px) {
    font-size: 16px;
    margin-bottom: 30px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
  margin-bottom: 15px;

  @media (min-width: 1024px) {
    gap: 20px;
    margin-bottom: 25px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  width: 100%;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #444;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px 8px 32px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
  box-sizing: border-box;
  height: 40px;

  @media (min-width: 1024px) {
    height: 48px;
    font-size: 16px;
    padding: 10px 16px 10px 40px;
  }

  &:focus {
    border-color: #ff6b6b;
    outline: none;
    box-shadow: 0 0 0 4px rgba(255, 107, 107, 0.1);
  }

  &.error {
    border-color: #ff4646;
    box-shadow: 0 0 0 4px rgba(255, 70, 70, 0.1);
  }
`;

const InputIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 16px;
`;

const ErrorText = styled.span`
  color: #ff4646;
  font-size: 13px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #ff6b6b, #ff8e53);
  color: white;
  padding: 10px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  margin-top: 10px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 1024px) {
    height: 48px;
    font-size: 17px;
    margin-top: 15px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(255, 107, 107, 0.2);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const RegisterLink = styled(Link)`
  color: #ff6b6b;
  text-align: center;
  display: block;
  margin-top: 15px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  padding: 6px;
  border-radius: 8px;
  font-size: 14px;

  &:hover {
    color: #ff8e53;
    background: rgba(255, 107, 107, 0.1);
  }
`;

const ForgotPasswordLink = styled(Link)`
  color: #666;
  text-align: right;
  display: block;
  margin-top: 6px;
  text-decoration: none;
  font-size: 12px;
  transition: all 0.3s ease;

  &:hover {
    color: #ff6b6b;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin: 15px 0;
  color: #757575;
  font-size: 13px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e0e0e0;
  }
`;

const GoogleSection = styled.div`
  margin: 0 0 15px 0;
`;

const RequiredAsterisk = styled.span`
  color: #ff4646;
  margin-left: -5px;
  font-size: 1em;
`;

interface LoginForm {
  email: string;
  password: string;
}

interface ValidationErrors {
  email?: string;
  password?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginForm>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [loading, setLoading] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.email) {
      newErrors.email = 'Oops! You forgot to add your email.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Oops! You forgot to enter your password.';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Too short — your password must be 6 characters minimum.';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(formData.password)) {
      newErrors.password = 'Your password needs to be stronger: add a capital letter, a lowercase letter, a number, and a special symbol.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof ValidationErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const response = await loginUser(formData);
      console.log("🚀 ~ handleSubmit ~ response:", response)

      if (response && response.user) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        toast.success('Login successful!');
        navigate(APP_ROUTES.DASHBOARD);
      } else {
        toast.error('Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error((error as any)?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      console.log("Google credential received:", credentialResponse);
      const response = await googleAuth(credentialResponse.credential);
      console.log("Google auth response:", response);

      if (!response) {
        console.error("No response received from googleAuth");
        toast.error('Login failed: No response from server');
        return;
      }

      if (!response.data) {
        console.error("No data in response:", response);
        toast.error('Login failed: Invalid response format');
        return;
      }

      const user = response.data.user;
      const token = user.token;


      if (!token) {
        console.error("No token in response data:", response.data);
        toast.error('Login failed: No token received');
        return;
      }

      if (!user) {
        console.error("No user in response data:", response.data);
        toast.error('Login failed: No user data received');
        return;
      }

      // Store the token and user data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      console.log("Token and user data stored successfully");
      console.log("Attempting to navigate to:", APP_ROUTES.DASHBOARD);

      toast.success('Google login successful!');

      // Force a hard navigation to dashboard
      window.location.href = APP_ROUTES.DASHBOARD;

    } catch (error) {
      console.error('Google login error:', error);
      toast.error(`Google login failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <LoginContainer>
      <HomeLink to="/">
        <FontAwesomeIcon icon={faHome} /> Back to Home
      </HomeLink>
      <LoginCard>
        <Title>Welcome Back</Title>
        <Subtitle>Sign in to continue to your account</Subtitle>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              <FontAwesomeIcon icon={faEnvelope} /> Email<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
                placeholder="Enter your email"
              />
            </InputWrapper>
            {errors.email && <ErrorText>{errors.email}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>
              <FontAwesomeIcon icon={faLock} /> Password<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'error' : ''}
                placeholder="Enter your password"
              />
            </InputWrapper>
            {errors.password && <ErrorText>{errors.password}</ErrorText>}
            <ForgotPasswordLink to="/forgot-password">
              Forgot your password?
            </ForgotPasswordLink>
          </FormGroup>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </SubmitButton>
        </Form>

        <Divider>or continue with</Divider>

        <GoogleSection>
          <GoogleAuthButton onSuccess={handleGoogleSuccess} />
        </GoogleSection>

        <RegisterLink to="/register">
          Don't have an account? Create one now
        </RegisterLink>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login; 